import { LitElement, html, css, nothing } from "lit";
import { customElement, property, state } from "lit/decorators.js";

/**
 * One label/value row. Values are rendered monospace because the rows this is
 * built for — hashes, object keys, query ids, fingerprints, digests — are read
 * character by character and compared by eye.
 */
export interface BnKvRow {
  label: string;
  /** The value. Omit it for a field that is genuinely not present. */
  value?: string;
  /**
   * The value exists but is not in the response. Secrets are never round-tripped
   * to the browser, so the row shows *that* there is a value without showing it.
   * A redacted row has no copy button — there is nothing to copy.
   */
  redacted?: boolean;
  /** Set false to suppress the copy button on a row that has a value. */
  copyable?: boolean;
}

/** Detail of bn-kv's `bn-copy` event. */
export interface BnKvCopyDetail {
  label: string;
  value: string;
}

/**
 * bn-kv is the record-detail definition list: label on the left, monospace value
 * on the right, a copy button per row, and a redacted state for values the API
 * deliberately does not return.
 *
 * It is the body of the audit record drawer (`04` §7.2) and of every "what
 * exactly is this object" pane. Rows are data, so the same component renders
 * four fields or forty. Copying uses the async clipboard API and confirms in
 * place — and says so honestly when the clipboard is unavailable, rather than
 * showing a checkmark for something that did not happen.
 *
 * Properties:
 * - `rows`         — `BnKvRow[]`, in display order; property-only
 * - `redactedText` — what a redacted row shows in place of the value
 * - `redactedHint` — the redaction chip's tooltip
 * - `unsetText`    — what a row with no value at all shows
 *
 * Events:
 * - `bn-copy` — `CustomEvent<BnKvCopyDetail>`, bubbles and composed, fired on a
 *   successful copy. Named `bn-copy` rather than `copy` so it cannot be confused
 *   with the platform's own clipboard event on the way up the tree.
 *
 *   <bn-kv
 *     .rows=${[
 *       { label: "Hash", value: record.hash },
 *       { label: "Prior hash", value: record.prior_hash },
 *       { label: "Object key", value: record.object_key },
 *       { label: "Admin token", redacted: true },
 *       { label: "Rewritten SQL" },
 *     ]}
 *     @bn-copy=${(e: CustomEvent<BnKvCopyDetail>) => toast(`${e.detail.label} copied`)}
 *   ></bn-kv>
 */
@customElement("bn-kv")
export class BnKv extends LitElement {
  /** The rows, in display order. Set as a property, never an attribute. */
  @property({ attribute: false }) rows: BnKvRow[] = [];
  /** What a redacted row shows in place of the value. */
  @property() redactedText = "set · not shown";
  /** Why it is not shown. Rendered as the redaction chip's tooltip. */
  @property() redactedHint = "This value exists but is never sent to the browser.";
  /** What a row with no value at all shows. */
  @property() unsetText = "not set";

  @state() private copied: { index: number; ok: boolean } | null = null;

  private resetTimer = 0;

  static override styles = css`
    :host {
      display: block;
      font-family: var(--bino-font-sans);
    }
    dl {
      display: grid;
      grid-template-columns: minmax(7rem, 14rem) minmax(0, 1fr);
      margin: 0;
    }
    dt,
    dd {
      border-bottom: 1px solid var(--bino-border);
      padding: var(--bino-space-sm) 0;
      min-width: 0;
    }
    dt {
      font-size: var(--bino-font-size-base);
      color: var(--bino-text-secondary);
      padding-right: var(--bino-space-md);
    }
    dd {
      display: flex;
      align-items: flex-start;
      gap: var(--bino-space-sm);
      margin: 0;
    }
    dl > dt:last-of-type,
    dl > dd:last-of-type {
      border-bottom: none;
    }
    .value {
      flex: 1 1 auto;
      font-family: var(--bino-font-mono);
      font-size: var(--bino-font-size-base);
      color: var(--bino-text);
      overflow-wrap: anywhere;
      white-space: pre-wrap;
    }
    .unset {
      flex: 1 1 auto;
      font-size: var(--bino-font-size-base);
      color: var(--bino-text-secondary);
    }
    .redacted {
      flex: 0 0 auto;
      display: inline-flex;
      align-items: center;
      gap: var(--bino-space-xs);
      font-size: var(--bino-font-size-sm);
      font-weight: 600;
      color: var(--bino-neutral);
      background: var(--bino-neutral-bg);
      border: 1px dashed var(--bino-border-light);
      border-radius: var(--bino-radius-pill);
      padding: 0.0625rem var(--bino-space-sm);
      cursor: help;
    }
    .copy {
      flex: none;
      display: inline-flex;
      align-items: center;
      justify-content: center;
      width: 1.5rem;
      height: 1.5rem;
      padding: 0;
      background: none;
      border: 1px solid transparent;
      border-radius: var(--bino-radius-sm);
      color: var(--bino-text-secondary);
      cursor: pointer;
      transition:
        color var(--bino-transition-fast),
        background var(--bino-transition-fast),
        border-color var(--bino-transition-fast);
    }
    .copy:hover {
      color: var(--bino-text);
      background: var(--bino-surface-hover);
      border-color: var(--bino-border);
    }
    .copy:focus-visible {
      outline: none;
      border-color: var(--bino-primary);
      box-shadow: 0 0 0 3px var(--bino-focus-ring);
    }
    .copy.ok {
      color: var(--bino-success);
    }
    .copy.failed {
      color: var(--bino-error);
    }
    .copy svg {
      width: 0.875rem;
      height: 0.875rem;
      stroke: currentColor;
      stroke-width: 1.5;
      fill: none;
      stroke-linecap: round;
      stroke-linejoin: round;
    }
    .sr {
      position: absolute;
      width: 1px;
      height: 1px;
      margin: -1px;
      padding: 0;
      overflow: hidden;
      white-space: nowrap;
      clip-path: inset(50%);
      border: 0;
    }
    @media (max-width: 32rem) {
      dl {
        grid-template-columns: minmax(0, 1fr);
      }
      dt {
        border-bottom: none;
        padding-bottom: 0;
      }
      dd {
        padding-top: var(--bino-space-xs);
      }
    }
  `;

  override disconnectedCallback() {
    super.disconnectedCallback();
    window.clearTimeout(this.resetTimer);
  }

  private async copy(row: BnKvRow, index: number) {
    const value = row.value ?? "";
    try {
      if (!navigator.clipboard) throw new Error("clipboard unavailable");
      await navigator.clipboard.writeText(value);
      this.copied = { index, ok: true };
      this.dispatchEvent(
        new CustomEvent<BnKvCopyDetail>("bn-copy", {
          detail: { label: row.label, value },
          bubbles: true,
          composed: true,
        }),
      );
    } catch {
      // Clipboard access is denied outside a secure context. Say so rather than
      // showing a confirmation for something that did not happen.
      this.copied = { index, ok: false };
    }
    window.clearTimeout(this.resetTimer);
    this.resetTimer = window.setTimeout(() => (this.copied = null), 1400);
  }

  private status(): string {
    if (!this.copied) return "";
    const row = this.rows[this.copied.index];
    if (!row) return "";
    return this.copied.ok ? `${row.label} copied` : `${row.label} could not be copied`;
  }

  override render() {
    return html`
      <dl>
        ${this.rows.map((row, i) => {
          const showCopy = !row.redacted && !!row.value && row.copyable !== false;
          const mark = this.copied?.index === i ? this.copied : null;
          return html`
            <dt>${row.label}</dt>
            <dd>
              ${row.redacted
                ? html`<span class="redacted" title=${this.redactedHint}>${this.redactedText}</span>`
                : row.value
                  ? html`<span class="value">${row.value}</span>`
                  : html`<span class="unset">${this.unsetText}</span>`}
              ${showCopy
                ? html`
                    <button
                      class="copy ${mark ? (mark.ok ? "ok" : "failed") : ""}"
                      type="button"
                      title=${mark ? (mark.ok ? "Copied" : "Copy failed") : "Copy"}
                      aria-label=${`Copy ${row.label}`}
                      @click=${() => this.copy(row, i)}
                    >
                      ${mark?.ok
                        ? html`<svg viewBox="0 0 14 14" aria-hidden="true">
                            <path d="M2.5 7.5 5.5 10.5 11.5 3.5"></path>
                          </svg>`
                        : html`<svg viewBox="0 0 14 14" aria-hidden="true">
                            <rect x="5" y="5" width="8" height="8" rx="2"></rect>
                            <path d="M10 4.5V3a2 2 0 0 0-2-2H3a2 2 0 0 0-2 2v5a2 2 0 0 0 2 2h1.5"></path>
                          </svg>`}
                    </button>
                  `
                : nothing}
            </dd>
          `;
        })}
      </dl>
      <span class="sr" role="status" aria-live="polite">${this.status()}</span>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    "bn-kv": BnKv;
  }
}
